import {
  GetAccountResponse,
  GetAccountsResponse,
} from '@features/accounts/api';
import { getBalanceFetches } from '@features/accounts/api/balance-fetches/get-balance-fetches';
import { postBalanceFetches } from '@features/accounts/api/balance-fetches/post-balance-fetches';
import { GetExternalAccountsResponse } from '@features/accounts/api/external-accounts/get-external-accounts';
import { BalanceFetchAccount } from '@features/accounts/types';
import { PerformanceData } from '@features/performance/types/get-performance-data-response';
import { queryClient } from '@src/lib/react-query';
import { useRefreshAccountJobsStore } from '@src/stores';
import { useMutation } from '@tanstack/react-query';
import { differenceInMinutes } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { Icons, Id, toast, Zoom } from 'react-toastify';

export default function UseRefreshAccounts() {
  const [toastId, setToastId] = useState<Id>();
  const toastCompletedId = 'refresh-accounts-completed';
  // Store active intervals so we can clean them up
  const activeIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const {
    removeAllRefreshAccountJobs,
    updateRefreshAccountJobStatus,
    updateRefreshAccountJobId,
    markJobsCompleted,
  } = useRefreshAccountJobsStore.use.actions();
  const jobs = useRefreshAccountJobsStore.use.refreshAccountJobs();

  const failAccount = (accountId: string) => {
    updateRefreshAccountJobStatus({
      accountId,
      status: 'failed',
    });
  };

  // Helper function to clear interval for specific accounts
  const clearAccountInterval = (accountId: string) => {
    const intervalId = activeIntervalsRef.current.get(accountId);
    if (intervalId) {
      clearInterval(intervalId);
      activeIntervalsRef.current.delete(accountId);
    }
  };

  const callLoadingToast = () => {
    const id = toast.loading('Your accounts are being refreshed...', {
      toastId: 'refresh-accounts',
      className: 'refresh-accounts-toast',
      closeButton: true,
      progress: undefined,
      icon: Icons.spinner,
      transition: Zoom,
      onClose: () => {
        setToastId(undefined);
      },
    });

    setToastId(id);
  };

  const callSuccessToast = (toastMessage: string) => {
    toastId
      ? toast.update(toastId, {
          toastId: toastCompletedId,
          render: toastMessage,
          isLoading: false,
          type: 'success',
          icon: Icons.success,
          autoClose: 5000,
        })
      : toast(toastMessage, {
          className: 'refresh-accounts-toast',
          toastId: toastCompletedId,
          isLoading: false,
          type: 'success',
          icon: Icons.success,
          autoClose: 5000,
        });
  };

  const callWarningToast = (toastMessage: string) => {
    toastId
      ? toast.update(toastId, {
          toastId: toastCompletedId,
          render: toastMessage,
          isLoading: false,
          type: 'warning',
          icon: Icons.warning,
          autoClose: 5000,
        })
      : toast(toastMessage, {
          className: 'refresh-accounts-toast',
          toastId: toastCompletedId,
          isLoading: false,
          type: 'warning',
          icon: Icons.warning,
          autoClose: 5000,
        });
  };

  const updateAccountsQueryCache = ({
    accounts,
    isExternal,
    accountId,
  }: {
    accounts: BalanceFetchAccount[];
    isExternal?: boolean;
    accountId: string;
  }) => {
    if (isExternal) {
      queryClient.setQueryData(
        ['externalAccounts', 'list'],
        (
          oldData: GetExternalAccountsResponse | undefined,
        ): GetExternalAccountsResponse | undefined => {
          if (!oldData) {
            return oldData;
          }
          return [...oldData].map((oldAccount) => {
            const foundRefreshedAccount = accounts?.find(
              (refreshedAccount) => refreshedAccount.uuid === oldAccount.uuid,
            );
            if (foundRefreshedAccount) {
              return {
                ...oldAccount,
                balanceUpdatedAt: foundRefreshedAccount.updatedAt,
                totalBalanceCents: foundRefreshedAccount.totalBalanceCents,
                availableBalanceCents:
                  foundRefreshedAccount.availableBalanceCents,
              };
            }
            return oldAccount;
          });
        },
      );
    }
    accounts.map((account) => {
      // update account cache
      queryClient.setQueryData(
        ['accounts', accountId],
        (oldData: GetAccountResponse): GetAccountResponse | undefined => {
          if (oldData) {
            return {
              ...oldData,
              balanceUpdatedAt: account.updatedAt,
              totalBalanceCents: account.totalBalanceCents,
              availableBalanceCents: account.availableBalanceCents,
            };
          }
        },
      );
      // Update accounts list cache
      queryClient.setQueryData(
        ['accounts', 'list'],
        (
          oldData: GetAccountsResponse | undefined,
        ): GetAccountsResponse | undefined => {
          if (!oldData) {
            return oldData;
          }
          const { visible: oldVisibleData, all } = oldData;
          if (
            oldVisibleData?.length > 0 &&
            oldVisibleData.some((oldAccount) => oldAccount.uuid === accountId)
          ) {
            const foundAccount = oldVisibleData.findIndex(
              (oldAccount) => oldAccount.uuid === accountId,
            );
            const clonedOldData = [...oldVisibleData];
            clonedOldData[foundAccount] = {
              ...clonedOldData[foundAccount],
              balanceUpdatedAt: account.updatedAt,
              totalBalanceCents: account.totalBalanceCents,
              availableBalanceCents: account.availableBalanceCents,
            };
            return {
              visible: clonedOldData,
              all,
            };
          }
        },
      );
    });
  };

  const updatePerformanceDataQueryCache = (totalBalance: string) => {
    queryClient.setQueryData(
      ['performanceData'],
      (oldData: PerformanceData): PerformanceData => {
        if (!oldData) {
          return oldData; // Handle cases where oldData might be undefined
        }

        return {
          ...oldData,
          totalBalance,
        };
      },
    );
  };

  const { mutate } = useMutation({
    mutationFn: postBalanceFetches,
    onMutate: (context: string) => {
      updateRefreshAccountJobStatus({ accountId: context, status: 'pending' });
      // show pending toast
      toast.dismiss(toastCompletedId);
      callLoadingToast();
    },
    onSuccess: (data, accountId) => {
      if (data.status === 'fresh') {
        updateRefreshAccountJobStatus({
          accountId: accountId,
          status: 'complete',
        });
        return;
      }

      updateRefreshAccountJobId({ accountId, id: data.jobId });

      // Clear any existing interval for this account first
      clearAccountInterval(accountId);

      const stopAndFail = () => {
        clearAccountInterval(accountId);
        failAccount(accountId);
      };

      const intervalId = setInterval(async () => {
        // Read the job from the store, not the render-time closure
        const currentJob =
          useRefreshAccountJobsStore.getState().refreshAccountJobs[accountId];

        // Check if 10 minutes have passed since job creation
        if (currentJob?.createdAt) {
          const minutesElapsed = differenceInMinutes(
            new Date(),
            new Date(currentJob.createdAt),
          );

          if (minutesElapsed >= 10) {
            stopAndFail();
            return;
          }
        }

        // Only a failed fetch fails the account, not a throw while
        // handling the response
        await getBalanceFetches({ accountId, jobId: data.jobId }).then(
          (res) => {
            if (res.status === 'failed') {
              stopAndFail();
              return;
            }

            if (res.status === 'complete') {
              clearAccountInterval(accountId);
              updateRefreshAccountJobStatus({
                accountId: accountId,
                status: 'complete',
              });

              // update performance data cache
              if (!currentJob?.isExternal && res?.savings?.totalBalance) {
                updatePerformanceDataQueryCache(res.savings.totalBalance);
              }

              // Update Accounts List and Id cache
              updateAccountsQueryCache({
                accounts: res.accounts,
                isExternal: currentJob?.isExternal,
                accountId: accountId,
              });
            }
          },
          (error: unknown) => {
            console.error(error);
            stopAndFail();
          },
        );
      }, 5000);

      // Store the interval ID so we can clean it up later
      activeIntervalsRef.current.set(accountId, intervalId);
    },
    // Failures show on the account card; 401s redirect via the MutationCache
    onError: (error, accountId) => {
      console.error(error);
      clearAccountInterval(accountId);
      failAccount(accountId);
    },
  });

  // Cleanup all intervals when component unmounts
  useEffect(() => {
    return () => {
      // Clear all active intervals when the component unmounts
      activeIntervalsRef.current.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      activeIntervalsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    // When all jobs are either completed or failed, show completed toast
    const jobList = Object.values(jobs);
    if (
      jobList.length &&
      jobList.every((job) => job.status !== 'init' && job.status !== 'pending')
    ) {
      const totalJobs = jobList.length;
      const successfulJobs = jobList.filter(
        (job) => job.status === 'complete',
      ).length;
      const toastMessage = `${successfulJobs} of ${totalJobs} accounts refreshed successfully.`;

      if (successfulJobs === totalJobs) {
        callSuccessToast(toastMessage);
      } else {
        callWarningToast(toastMessage);
      }
      // Clear the store
      removeAllRefreshAccountJobs();
      markJobsCompleted();
    }
  }, [jobs]);

  return { mutate };
}
